import { useCallback, useEffect, useRef, useState } from 'react';
import { studioApi, idempotencyKey } from '../../../../../data/studioApi';

const PER_PAGE = 20;

// GET /automations with its filters, or the trash (`trashed`). Rows change in place: pausing,
// activating, trashing and restoring read the page and the plan's usage again.
export default function useAutomationList(s, trashed) {
  const profileId = s.profile.id;
  const [page, setPage] = useState(null);
  const [failed, setFailed] = useState(false);
  const [number, setNumber] = useState(1);
  const [query, setQueryState] = useState('');
  const [debounced, setDebounced] = useState('');
  const [channel, setChannelState] = useState(null);
  const [status, setStatusState] = useState(null);
  const [sort, setSortState] = useState('-updated');
  const reads = useRef(0);

  const load = useCallback(async () => {
    const read = ++reads.current;
    try {
      const next = await studioApi.automations({ profile_id: profileId, page: number, per_page: PER_PAGE, q: debounced.trim() || undefined, channel: channel || undefined, status: status || undefined, sort, trashed: trashed || undefined });
      if (read !== reads.current) return;
      setPage(next);
      setFailed(false);
    } catch { if (read === reads.current) setFailed(true); }
  }, [profileId, number, debounced, channel, status, sort, trashed]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 280);
    return () => clearTimeout(timer);
  }, [query]);

  // A new filter starts from the first page.
  const fresh = setter => value => { setNumber(1); setter(value); };
  const setQuery = fresh(setQueryState);
  const setChannel = fresh(setChannelState);
  const setStatus = fresh(setStatusState);
  const setSort = fresh(setSortState);
  const clearFilters = () => { setNumber(1); setQueryState(''); setDebounced(''); setChannelState(null); setStatusState(null); };
  const filtering = !!(debounced.trim() || channel || status);

  // An action on a row: errors go to the shell's banner, and whatever happened is read again.
  const act = async work => {
    const done = await s.act(work);
    load();
    s.automations.refresh();
    return done;
  };
  const trash = automation => act(() => studioApi.deleteAutomation(automation.id, idempotencyKey('automation-trash')));
  const restore = automation => act(() => studioApi.restoreAutomation(automation.id, idempotencyKey('automation-restore')));
  const pause = automation => act(() => studioApi.updateAutomation(automation.id, { expected_version: automation.version, status: 'paused' }, idempotencyKey('automation-pause')));

  return { page, failed, loading: !page && !failed, number, setNumber, query, setQuery, channel, setChannel, status, setStatus, sort, setSort, clearFilters, filtering, retry: load, trash, restore, pause };
}
